import { z } from 'zod'

export const clinicInfoSchema = z
  .object({
    dentistryName: z.string().optional(),
    dentistry_name: z.string().optional(),
    dentistryAddress: z.string().optional(),
    dentistry_address: z.string().optional(),
    dentistryPhone: z.string().optional(),
    dentistryEmail: z.string().optional(),
    emergencyPhone: z.string().optional(),
  })
  .passthrough()

const validTimestampSchema = z
  .number()
  .int()
  .positive()
  .max(8_640_000_000_000_000)

const nonnegativeTimestampSchema = z
  .number()
  .int()
  .nonnegative()
  .max(8_640_000_000_000_000)

const metricSchema = z.number().int().nonnegative().finite()
const calendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const [year, month, day] = value.split('-').map(Number)
    const date = new Date(Date.UTC(year ?? 0, (month ?? 0) - 1, day))
    return date.toISOString().slice(0, 10) === value
  })
export const timeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: value }).format(0)
      return true
    } catch {
      return false
    }
  })

export const realtimeOverviewSchema = z
  .object({
    schemaVersion: z.literal(1),
    status: z.enum(['warming', 'ready', 'stale']),
    window: z
      .object({
        kind: z.literal('clinic-calendar-days'),
        timeZone: timeZoneSchema,
        startDate: calendarDateSchema,
        endDate: calendarDateSchema,
        startAtMs: validTimestampSchema,
        endBeforeMs: validTimestampSchema,
      })
      .strict(),
    metrics: z
      .object({
        activeConversations: metricSchema,
        messages: metricSchema,
        newConversations: metricSchema,
        finalizedCalls: metricSchema,
        bookedAppointments: metricSchema,
      })
      .strict(),
    updatedAtMs: validTimestampSchema,
    coverageStartedAtMs: nonnegativeTimestampSchema,
    complete: z.boolean(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.window.endBeforeMs <= value.window.startAtMs) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['window', 'endBeforeMs'],
        message: 'window end must follow its start',
      })
    }
    if (value.window.endDate < value.window.startDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['window', 'endDate'],
        message: 'window dates are reversed',
      })
    }
    if ((value.status === 'ready') !== value.complete) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['complete'],
        message: 'ready and complete must agree',
      })
    }
    if (value.complete && value.coverageStartedAtMs < 1) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['coverageStartedAtMs'],
        message: 'complete snapshots require producer coverage',
      })
    }
  })

export type ClinicIdentity = {
  name: string | null
  address: string | null
}

export type RealtimeOverview = z.infer<typeof realtimeOverviewSchema>
